/**
 * Category Navigation with Tabs
 * - Tab hover 切換內容
 * - 自動輪播功能
 * - 左右箭頭導航
 */

(function() {
  // 防止重複初始化
  if (window.CategoryNavigationTabsLoaded) return;
  window.CategoryNavigationTabsLoaded = true;

class CategoryNavigationTabs {
  constructor(container) {
    this.container = container;
    this.tabs = Array.from(container.querySelectorAll('.category-tab'));
    this.panels = Array.from(container.querySelectorAll('.category-tab-panel'));
    this.prevArrow = container.querySelector('.category-nav-prev');
    this.nextArrow = container.querySelector('.category-nav-next');

    this.currentIndex = 0;
    this.autoplayTimer = null;
    this.hoverTimer = null;
    this.isHovering = false;
    this.progressAnimationFrame = null;
    this.progressStartTime = null;

    // 從 data 屬性獲取設置
    this.autoplayEnabled = this.container.dataset.autoplay === 'true';
    this.autoplaySpeed = parseInt(this.container.dataset.autoplaySpeed) * 1000 || 5000;
    this.hoverPauseEnabled = this.container.dataset.hoverPause === 'true';
    this.showProgress = this.container.dataset.showProgress === 'true';

    this.init();
  }

  init() {
    if (this.tabs.length <= 1) return;

    // 設置初始狀態
    this.goToTab(0);

    // 綁定事件
    this.setupEventListeners();

    // 啟動自動切換
    if (this.autoplayEnabled) {
      this.startAutoplay();
    }
  }

  setupEventListeners() {
    // Tab hover 切換
    this.tabs.forEach((tab, index) => {
      // Hover 事件
      tab.addEventListener('mouseenter', () => {
        // 清除之前的 hover timer
        if (this.hoverTimer) {
          clearTimeout(this.hoverTimer);
        }

        // 延遲 200ms 切換，避免快速滑過時頻繁切換
        this.hoverTimer = setTimeout(() => {
          this.goToTab(index);
          // 如果沒有啟用 hover pause，則在 tab hover 時停止自動播放
          if (!this.hoverPauseEnabled) {
            this.stopAutoplay();
          }
        }, 200);
      });

      tab.addEventListener('mouseleave', () => {
        // 清除 hover timer
        if (this.hoverTimer) {
          clearTimeout(this.hoverTimer);
          this.hoverTimer = null;
        }
      });

      // Click 事件（移動端）
      tab.addEventListener('click', (e) => {
        e.preventDefault();
        this.goToTab(index);
        // 點擊時總是停止自動播放
        this.stopAutoplay();
      });
    });

    // 箭頭導航
    if (this.prevArrow) {
      this.prevArrow.addEventListener('click', () => {
        this.prev();
        this.stopAutoplay();
      });
    }

    if (this.nextArrow) {
      this.nextArrow.addEventListener('click', () => {
        this.next();
        this.stopAutoplay();
      });
    }

    // Hover pause 功能
    if (this.hoverPauseEnabled) {
      this.container.addEventListener('mouseenter', () => {
        this.isHovering = true;
        this.stopAutoplay();
      });

      this.container.addEventListener('mouseleave', () => {
        this.isHovering = false;
        if (this.autoplayEnabled) {
          this.startAutoplay();
        }
      });
    } else {
      // 如果沒有啟用 hover pause，只在離開時重新啟動
      this.container.addEventListener('mouseleave', () => {
        if (this.autoplayEnabled) {
          this.startAutoplay();
        }
      });
    }

    // 鍵盤導航
    document.addEventListener('keydown', (e) => {
      if (!this.isInViewport()) return;

      if (e.key === 'ArrowLeft') {
        this.prev();
        this.stopAutoplay();
      } else if (e.key === 'ArrowRight') {
        this.next();
        this.stopAutoplay();
      }
    });

    // 觸控滑動支持（針對內容區域）
    this.setupTouchEvents();
  }

  setupTouchEvents() {
    const content = this.container.querySelector('.category-nav-content');
    if (!content) return;

    let startX = 0;
    let startY = 0;
    let currentX = 0;
    let currentY = 0;
    let isDragging = false;
    let hasMoved = false;
    let startTime = 0;

    content.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      currentX = startX;
      currentY = startY;
      isDragging = true;
      hasMoved = false;
      startTime = Date.now();
    }, { passive: true });

    content.addEventListener('touchmove', (e) => {
      if (!isDragging) return;
      currentX = e.touches[0].clientX;
      currentY = e.touches[0].clientY;

      // 檢測是否有明顯移動（超過 10px）
      const moveX = Math.abs(currentX - startX);
      const moveY = Math.abs(currentY - startY);

      if (moveX > 10 || moveY > 10) {
        hasMoved = true;

        // 如果是水平滑動，停止自動播放
        if (moveX > moveY) {
          this.stopAutoplay();
        }
      }
    }, { passive: true });

    content.addEventListener('touchend', () => {
      if (!isDragging) return;

      const diff = startX - currentX;
      const diffY = Math.abs(startY - currentY);
      const threshold = 50;
      const timeElapsed = Date.now() - startTime;

      // 只有在以下情況才觸發滑動切換：
      // 1. 手指有明顯移動 (hasMoved = true)
      // 2. 水平移動距離超過閾值 (50px)
      // 3. 水平移動大於垂直移動（確保是水平滑動）
      // 4. 不是快速點擊（時間超過 100ms）
      if (hasMoved && Math.abs(diff) > threshold && Math.abs(diff) > diffY && timeElapsed > 100) {
        if (diff > 0) {
          this.next();
        } else {
          this.prev();
        }
      }

      isDragging = false;
      hasMoved = false;

      if (this.autoplayEnabled) {
        this.startAutoplay();
      }
    });
  }

  prev() {
    const newIndex = this.currentIndex > 0
      ? this.currentIndex - 1
      : this.tabs.length - 1;
    this.goToTab(newIndex);
  }

  next() {
    const newIndex = this.currentIndex < this.tabs.length - 1
      ? this.currentIndex + 1
      : 0;
    this.goToTab(newIndex);
  }

  goToTab(index) {
    if (index === this.currentIndex) return;

    // 重置舊的進度條
    if (this.showProgress) {
      const oldPanel = this.panels[this.currentIndex];
      if (oldPanel) {
        const oldProgressFill = oldPanel.querySelector('.category-progress-fill');
        if (oldProgressFill) {
          oldProgressFill.style.width = '0%';
        }
      }
    }

    this.currentIndex = index;

    // 更新 Tab 狀態
    this.tabs.forEach((tab, i) => {
      if (i === index) {
        tab.classList.add('active');
        tab.setAttribute('aria-selected', 'true');
      } else {
        tab.classList.remove('active');
        tab.setAttribute('aria-selected', 'false');
      }
    });

    // 更新 Panel 狀態
    this.panels.forEach((panel, i) => {
      if (i === index) {
        panel.classList.add('active');
      } else {
        panel.classList.remove('active');
      }
    });

    // 確保 active tab 在可見範圍內
    this.scrollTabIntoView(index);
  }

  scrollTabIntoView(index) {
    const activeTab = this.tabs[index];
    if (!activeTab) return;

    const tabsContainer = this.container.querySelector('.category-nav-tabs');
    if (!tabsContainer) return;

    // 計算 tab 相對於容器的位置
    const tabLeft = activeTab.offsetLeft;
    const tabRight = tabLeft + activeTab.offsetWidth;
    const scrollLeft = tabsContainer.scrollLeft;
    const containerWidth = tabsContainer.offsetWidth;

    // 檢查 tab 是否在可見範圍內
    const isTabVisible =
      tabLeft >= scrollLeft &&
      tabRight <= scrollLeft + containerWidth;

    if (!isTabVisible) {
      // 計算需要滾動的位置
      // 將 tab 置於容器中央
      const scrollTo = tabLeft - (containerWidth / 2) + (activeTab.offsetWidth / 2);

      // 平滑滾動到目標位置
      tabsContainer.scrollTo({
        left: Math.max(0, scrollTo),
        behavior: 'smooth'
      });
    }
  }

  startAutoplay() {
    this.stopAutoplay();

    // 重置進度條
    if (this.showProgress) {
      this.resetProgressBar();
      this.startProgressAnimation();
    }

    this.autoplayTimer = setInterval(() => {
      this.next();
      // 每次切換後重新開始進度條動畫
      if (this.showProgress) {
        this.resetProgressBar();
        this.startProgressAnimation();
      }
    }, this.autoplaySpeed);
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
    // 停止進度條動畫
    if (this.progressAnimationFrame) {
      cancelAnimationFrame(this.progressAnimationFrame);
      this.progressAnimationFrame = null;
    }
  }

  resetProgressBar() {
    const activePanel = this.panels[this.currentIndex];
    if (!activePanel) return;

    const progressFill = activePanel.querySelector('.category-progress-fill');
    if (progressFill) {
      progressFill.style.width = '0%';
    }
  }

  startProgressAnimation() {
    const activePanel = this.panels[this.currentIndex];
    if (!activePanel) return;

    const progressFill = activePanel.querySelector('.category-progress-fill');
    if (!progressFill) return;

    this.progressStartTime = performance.now();

    const animate = (currentTime) => {
      const elapsed = currentTime - this.progressStartTime;
      const progress = Math.min((elapsed / this.autoplaySpeed) * 100, 100);

      progressFill.style.width = progress + '%';

      if (progress < 100) {
        this.progressAnimationFrame = requestAnimationFrame(animate);
      }
    };

    this.progressAnimationFrame = requestAnimationFrame(animate);
  }

  isInViewport() {
    const rect = this.container.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  }

  destroy() {
    this.stopAutoplay();
    if (this.hoverTimer) {
      clearTimeout(this.hoverTimer);
    }
    // 移除事件監聽器等清理工作
  }
}

// 初始化所有 Tab 導航
function initCategoryTabs() {
  const containers = document.querySelectorAll('.category-navigation-tabs');

  containers.forEach(container => {
    if (!container.dataset.initialized) {
      const instance = new CategoryNavigationTabs(container);
      container.tabsInstance = instance;
      container.dataset.initialized = 'true';
    }
  });
}

// DOM 載入完成後初始化
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCategoryTabs);
} else {
  initCategoryTabs();
}

// 監聽動態內容載入（例如 Shopify sections）
document.addEventListener('shopify:section:load', (event) => {
  const container = event.target.querySelector('.category-navigation-tabs');
  if (container && !container.dataset.initialized) {
    const instance = new CategoryNavigationTabs(container);
    container.tabsInstance = instance;
    container.dataset.initialized = 'true';
  }
});

// 監聽 section 卸載
document.addEventListener('shopify:section:unload', (event) => {
  const container = event.target.querySelector('.category-navigation-tabs');
  if (container && container.tabsInstance) {
    container.tabsInstance.destroy();
  }
});

})(); // End IIFE
